'use client';

import React, { useMemo } from 'react';
import { ConfigProvider, type ThemeConfig } from 'antd';

type Props = {
  primary?: string;
  children: React.ReactNode;
};

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const withAlpha = (color: string, alpha: number): string => {
  if (!HEX.test(color)) return color;

  const raw = color.slice(1);
  const full =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw;

  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const AntdThemeProvider: React.FC<Props> = ({ primary, children }) => {
  const theme = useMemo<ThemeConfig | undefined>(() => {
    if (!primary) return undefined;

    return {
      token: {
        colorPrimary: primary,
        colorLink: primary,
      },
      components: {
        Button: {
          defaultColor: primary,
          defaultBorderColor: primary,
          defaultHoverColor: primary,
          defaultHoverBorderColor: primary,
          defaultHoverBg: withAlpha(primary, 0.08),
          defaultActiveColor: primary,
          defaultActiveBorderColor: primary,
          defaultActiveBg: withAlpha(primary, 0.16),
        },
      },
    };
  }, [primary]);

  return <ConfigProvider theme={theme}>{children}</ConfigProvider>;
};

export default AntdThemeProvider;
